import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Settings, Plus, Edit3, Trash2, Star, StarOff, Type, FileText } from 'lucide-react';

interface EmailSignature {
  id: number;
  signatureName: string;
  signatureHtml: string;
  signatureText: string;
  isDefault: boolean;
  isActive: boolean;
}

interface EmailSignatureSelectorProps {
  signatures: EmailSignature[];
  selectedId: number | null;
  onSelect: (signatureId: number | null) => void;
  showManagementLink?: boolean;
}

interface SignatureFormData {
  signatureName: string;
  signatureHtml: string;
  signatureText: string;
  isDefault: boolean;
}

export function EmailSignatureSelector({
  signatures,
  selectedId,
  onSelect,
  showManagementLink = true,
}: EmailSignatureSelectorProps) {
  const { toast } = useToast();
  const [showManager, setShowManager] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSignature, setEditingSignature] = useState<EmailSignature | null>(null);
  const [formData, setFormData] = useState<SignatureFormData>({
    signatureName: '',
    signatureHtml: '',
    signatureText: '',
    isDefault: false,
  });

  // Create signature mutation
  const createSignatureMutation = useMutation({
    mutationFn: (data: SignatureFormData) => {
      return apiRequest('POST', '/api/email-signatures', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email-signatures'] });
      setShowCreateForm(false);
      setFormData({
        signatureName: '',
        signatureHtml: '',
        signatureText: '',
        isDefault: false,
      });
      toast({
        title: 'Signature created',
        description: 'Your email signature has been created successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: `Failed to create signature: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Update signature mutation
  const updateSignatureMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<SignatureFormData> }) => {
      return apiRequest('PUT', `/api/email-signatures/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email-signatures'] });
      setEditingSignature(null);
      setFormData({
        signatureName: '',
        signatureHtml: '',
        signatureText: '',
        isDefault: false,
      });
      toast({
        title: 'Signature updated',
        description: 'Your email signature has been updated successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: `Failed to update signature: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Delete signature mutation
  const deleteSignatureMutation = useMutation({
    mutationFn: (id: number) => {
      return apiRequest('DELETE', `/api/email-signatures/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email-signatures'] });
      toast({
        title: 'Signature deleted',
        description: 'Your email signature has been deleted successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: `Failed to delete signature: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Set default signature mutation
  const setDefaultMutation = useMutation({
    mutationFn: (id: number) => {
      return apiRequest('POST', `/api/email-signatures/${id}/set-default`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email-signatures'] });
      toast({
        title: 'Default signature updated',
        description: 'Your default signature has been updated successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: `Failed to set default signature: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  const handleEditSignature = (signature: EmailSignature) => {
    setEditingSignature(signature);
    setFormData({
      signatureName: signature.signatureName,
      signatureHtml: signature.signatureHtml,
      signatureText: signature.signatureText,
      isDefault: signature.isDefault,
    });
  };

  const handleSubmit = () => {
    if (!formData.signatureName.trim()) {
      toast({
        title: 'Error',
        description: 'Signature name is required.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.signatureText.trim() && !formData.signatureHtml.trim()) {
      toast({
        title: 'Error',
        description: 'Signature content is required.',
        variant: 'destructive',
      });
      return;
    }

    if (editingSignature) {
      updateSignatureMutation.mutate({ id: editingSignature.id, data: formData });
    } else {
      createSignatureMutation.mutate(formData);
    }
  };

  const handleCancel = () => {
    setEditingSignature(null);
    setShowCreateForm(false);
    setFormData({
      signatureName: '',
      signatureHtml: '',
      signatureText: '',
      isDefault: false,
    });
  };

  return (
    <div className="space-y-2">
      {/* Signature Selector */}
      <div className="flex items-center gap-2">
        <Select
          value={selectedId?.toString() || 'none'}
          onValueChange={(value) => onSelect(value === 'none' || !value ? null : parseInt(value))}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="Select signature..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No signature</SelectItem>
            {signatures.map((signature) => (
              <SelectItem key={signature.id} value={signature.id.toString()}>
                <div className="flex items-center gap-2">
                  {signature.signatureName}
                  {signature.isDefault && (
                    <Badge variant="secondary" className="text-xs">
                      Default
                    </Badge>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {showManagementLink && (
          <Dialog open={showManager} onOpenChange={setShowManager}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Manage Email Signatures</DialogTitle>
                <DialogDescription>
                  Create, edit, and manage your email signatures.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Create New Signature Button */}
                <Button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full"
                  disabled={showCreateForm || editingSignature !== null}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Signature
                </Button>

                {/* Create/Edit Form */}
                {(showCreateForm || editingSignature) && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">
                        {editingSignature ? 'Edit Signature' : 'Create New Signature'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label htmlFor="signatureName">Signature Name</Label>
                        <Input
                          id="signatureName"
                          value={formData.signatureName}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, signatureName: e.target.value }))
                          }
                          placeholder="e.g., Work Signature"
                        />
                      </div>

                      <div>
                        <Label htmlFor="signatureText">Text Content</Label>
                        <Textarea
                          id="signatureText"
                          value={formData.signatureText}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, signatureText: e.target.value }))
                          }
                          placeholder="Your name&#10;Your title&#10;Company name&#10;Phone: +44 123 456 7890"
                          rows={4}
                        />
                      </div>

                      <div>
                        <Label htmlFor="signatureHtml">HTML Content (Optional)</Label>
                        <Textarea
                          id="signatureHtml"
                          value={formData.signatureHtml}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, signatureHtml: e.target.value }))
                          }
                          placeholder="<div><strong>Your Name</strong><br/>Your Title<br/>Company Name</div>"
                          rows={4}
                        />
                      </div>

                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="isDefault"
                          checked={formData.isDefault}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, isDefault: e.target.checked }))
                          }
                        />
                        <Label htmlFor="isDefault">Set as default signature</Label>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={handleSubmit}
                          disabled={
                            createSignatureMutation.isPending || updateSignatureMutation.isPending
                          }
                        >
                          {editingSignature ? 'Update' : 'Create'} Signature
                        </Button>
                        <Button variant="outline" onClick={handleCancel}>
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Separator />

                {/* Existing Signatures */}
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold">Your Signatures</h3>
                  {signatures.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      No signatures created yet. Create your first signature above.
                    </p>
                  ) : (
                    signatures.map((signature) => (
                      <Card key={signature.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-medium">{signature.signatureName}</h4>
                                {signature.isDefault && (
                                  <Badge variant="default" className="text-xs">
                                    <Star className="h-3 w-3 mr-1" />
                                    Default
                                  </Badge>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground bg-muted p-2 rounded whitespace-pre-wrap">
                                {signature.signatureText || signature.signatureHtml}
                              </div>
                            </div>
                            <div className="flex gap-1 ml-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditSignature(signature)}
                                disabled={editingSignature !== null || showCreateForm}
                              >
                                <Edit3 className="h-4 w-4" />
                              </Button>
                              {!signature.isDefault && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setDefaultMutation.mutate(signature.id)}
                                  disabled={setDefaultMutation.isPending}
                                >
                                  <StarOff className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => deleteSignatureMutation.mutate(signature.id)}
                                disabled={deleteSignatureMutation.isPending}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Selected Signature Preview */}
      {selectedId && signatures.find((s) => s.id === selectedId) && (
        <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
          <div className="font-medium mb-1">Selected signature:</div>
          <div className="whitespace-pre-wrap">
            {signatures.find((s) => s.id === selectedId)?.signatureText}
          </div>
        </div>
      )}
    </div>
  );
}
